import { useState, useEffect } from 'react'
import { supabase } from './supabase'

export default function Profile({ session, onBack, onSignOut }) {
  const [profile, setProfile] = useState(null)
  const [golfProfile, setGolfProfile] = useState(null)
  const [form, setForm] = useState({ first_name: '', last_name: '', username: '', phone: '' })
  const [golfForm, setGolfForm] = useState({ handicap_index: '', ghin_number: '', home_course: '' })
  const [avatarUrl, setAvatarUrl] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savingGolf, setSavingGolf] = useState(false)
  const [error, setError] = useState(null)
  const [golfError, setGolfError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [golfSuccess, setGolfSuccess] = useState(null)
  const [golfExpanded, setGolfExpanded] = useState(false)


  useEffect(() => { fetchData() }, [session])

  const fetchData = async () => {
    const { data: p } = await supabase.from('profiles').select('*').eq('id', session.user.id).single()
    if (p) {
      setProfile(p)
      setForm({ first_name: p.first_name || '', last_name: p.last_name || '', username: p.username || '', phone: p.phone || '' })
      if (p.avatar_url) setAvatarUrl(p.avatar_url)
    }
    const { data: g } = await supabase.from('golf_profiles').select('*').eq('id', session.user.id).single()
    if (g) {
      setGolfProfile(g)
      setGolfForm({ handicap_index: g.handicap_index != null ? String(g.handicap_index) : '', ghin_number: g.ghin_number || '', home_course: g.home_course || '' })
    }
  }

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 2 * 1024 * 1024) return setError('Photo must be under 2MB.')
    setUploading(true)
    const ext = file.name.split('.').pop()
    const path = session.user.id + '.' + ext
    const { error: uploadError } = await supabase.storage.from('avatars').upload(path, file, { upsert: true })
    if (uploadError) { setUploading(false); return setError(uploadError.message) }
    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path)
    const url = urlData.publicUrl
    await supabase.from('profiles').update({ avatar_url: url }).eq('id', session.user.id)
    setAvatarUrl(url)
    setUploading(false)
  }

  const handleSaveProfile = async () => {
    setError(null)
    setSuccess(null)
    if (!form.first_name.trim() || !form.last_name.trim() || !form.username.trim()) return setError('First name, last name, and username are required.')
    if (!form.username.match(/^[a-zA-Z0-9_]+$/)) return setError('Username can only contain letters, numbers, and underscores.')
    setSaving(true)
    const { error: updateError } = await supabase.from('profiles').update({ first_name: form.first_name.trim(), last_name: form.last_name.trim(), username: form.username.toLowerCase().trim(), phone: form.phone.trim() }).eq('id', session.user.id)
    setSaving(false)
    if (updateError) {
      if (updateError.message.includes('unique')) return setError('That username is already taken.')
      return setError(updateError.message)
    }
    setSuccess('Profile saved successfully.')
  }

  const handleSaveGolf = async () => {
    setGolfError(null)
    setGolfSuccess(null)
    if (!golfForm.handicap_index && !golfForm.ghin_number) return setGolfError('Enter a handicap index or GHIN number.')
    setSavingGolf(true)
    const { error: upsertError } = await supabase.from('golf_profiles').upsert({ id: session.user.id, handicap_index: golfForm.handicap_index ? parseFloat(golfForm.handicap_index) : null, ghin_number: golfForm.ghin_number || null, home_course: golfForm.home_course || null })
    setSavingGolf(false)
    if (upsertError) return setGolfError(upsertError.message)
    setGolfSuccess('Golf profile saved.')
  }

  const initials = profile ? profile.first_name.charAt(0).toUpperCase() + profile.last_name.charAt(0).toUpperCase() : '?'

  return (
    <div style={styles.screen}>
      <div style={styles.body}>

        <div style={styles.avatarSection}>
          <label style={styles.avatarWrap}>
            {avatarUrl ? <img src={avatarUrl} alt="avatar" style={styles.avatarImg} /> : <div style={styles.avatarInitials}>{initials}</div>}
            <div style={styles.avatarOverlay}>{uploading ? '...' : 'Photo'}</div>
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarUpload} />
          </label>
          <div style={styles.avatarHint}>{uploading ? 'Uploading...' : 'Tap to change photo'}</div>
          <div style={styles.emailBadge}>{session.user.email}</div>
        </div>

        <div style={styles.section}>
          <div style={styles.sectionTitle}>ACCOUNT INFO</div>
          <div style={styles.card}>
            <div style={styles.row}>
              <div style={styles.halfField}>
                <label style={styles.label}>FIRST NAME</label>
                <input style={styles.input} value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} />
              </div>
              <div style={styles.halfField}>
                <label style={styles.label}>LAST NAME</label>
                <input style={styles.input} value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} />
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>USERNAME</label>
              <div style={styles.inputWrap}>
                <span style={styles.prefix}>@</span>
                <input style={{ ...styles.input, paddingLeft: 28 }} value={form.username} onChange={e => setForm({...form, username: e.target.value})} />
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>PHONE</label>
              <input style={styles.input} type="tel" placeholder="+1 (555) 000-0000" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
            </div>
            {error && <div style={styles.error}>{error}</div>}
            {success && <div style={styles.successMsg}>{success}</div>}
            <button style={{ ...styles.btn, opacity: saving ? 0.7 : 1 }} onClick={handleSaveProfile} disabled={saving}>
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </div>

        <div style={styles.section}>
          <div style={styles.sectionTitle}>GAME PROFILES</div>
          <div style={styles.card}>
            <div style={styles.gameProfileHeader} onClick={() => setGolfExpanded(v => !v)}>
              <span style={styles.gameProfileIcon}>Golf</span>
              <span style={styles.gameProfileName}>Golf Profile</span>
              {golfProfile && <span style={styles.verifiedBadge}>{golfProfile.ghin_number ? 'GHIN Verified' : 'Manual'}</span>}
              <span style={styles.chevron}>{golfExpanded ? '▲' : '▼'}</span>
            </div>
            {golfExpanded && (
              <>
                <div style={styles.divider} />
                <div style={styles.field}>
                  <label style={styles.label}>HANDICAP INDEX</label>
                  <input style={styles.input} type="number" step="0.1" min="-10" max="54" placeholder="e.g. 12.4" value={golfForm.handicap_index} onChange={e => setGolfForm({...golfForm, handicap_index: e.target.value})} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>GHIN NUMBER <span style={styles.optional}>(optional)</span></label>
                  <input style={styles.input} placeholder="e.g. 1234567" value={golfForm.ghin_number} onChange={e => setGolfForm({...golfForm, ghin_number: e.target.value})} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>HOME COURSE <span style={styles.optional}>(optional)</span></label>
                  <input style={styles.input} placeholder="e.g. Doral Golf Resort" value={golfForm.home_course} onChange={e => setGolfForm({...golfForm, home_course: e.target.value})} />
                </div>
                {golfError && <div style={styles.error}>{golfError}</div>}
                {golfSuccess && <div style={styles.successMsg}>{golfSuccess}</div>}
                <button style={{ ...styles.btnGolf, opacity: savingGolf ? 0.7 : 1 }} onClick={handleSaveGolf} disabled={savingGolf}>
                  {savingGolf ? 'Saving...' : 'Save Golf Profile'}
                </button>
              </>
            )}
          </div>
        </div>

        <div style={styles.section}>
          <div style={styles.sectionTitle}>ACCOUNT</div>
          <div style={styles.card}>
            <div>
              <div style={styles.accountLabel}>EMAIL ADDRESS</div>
              <div style={styles.accountValue}>{session.user.email}</div>
              <div style={styles.accountNote}>Contact support to change email</div>
            </div>
            <div style={styles.divider} />
            <button style={styles.signOutBtn} onClick={onSignOut}>Sign Out</button>
          </div>
        </div>

      </div>
    </div>
  )
}

const styles = {
  screen: { minHeight: '100vh', background: '#FDF9E7', display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif", overflowX: 'hidden', width: '100%' },
  header: { background: '#0A0A0A', padding: '48px 24px 20px', display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 },
  backBtn: { background: 'none', border: 'none', color: '#C19A6B', fontSize: 24, cursor: 'pointer', padding: 0, lineHeight: 1 },
  headerTitle: { fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 28, color: '#FDF9E7', letterSpacing: 2 },
  body: { flex: 1, padding: '20px 24px 48px', display: 'flex', flexDirection: 'column', gap: 20, overflowY: 'auto' },
  avatarSection: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '8px 0' },
  avatarWrap: { width: 88, height: 88, borderRadius: 44, cursor: 'pointer', position: 'relative', overflow: 'hidden', border: '3px solid #00563F' },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
  avatarInitials: { width: '100%', height: '100%', background: 'rgba(0,96,57,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700, color: '#00563F' },
  avatarOverlay: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.4)', textAlign: 'center', padding: '4px 0', fontSize: 12, color: 'white', fontWeight: 600 },
  avatarHint: { fontSize: 12, color: '#7A7570' },
  emailBadge: { background: '#F5F1E4', border: '1px solid #E0DDD4', borderRadius: 20, padding: '6px 16px', fontSize: 13, color: '#7A7570' },
  section: { display: 'flex', flexDirection: 'column', gap: 10 },
  sectionTitle: { fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 16, color: '#7A7570', letterSpacing: 2 },
  card: { background: '#FFFFFF', borderRadius: 20, padding: 20, border: '1px solid #E0DDD4', display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  row: { display: 'flex', gap: 12 },
  halfField: { flex: 1, display: 'flex', flexDirection: 'column', gap: 6 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 11, fontWeight: 700, color: '#7A7570', letterSpacing: 1.5, textTransform: 'uppercase' },
  optional: { fontSize: 11, fontWeight: 400, color: '#B0ACA4', letterSpacing: 0, textTransform: 'none' },
  input: { background: '#FDF9E7', border: '1.5px solid #D0CCC0', borderRadius: 12, padding: '12px 16px', fontSize: 15, color: '#1A1814', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
  inputWrap: { position: 'relative' },
  prefix: { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#7A7570', fontWeight: 700, fontSize: 16 },
  error: { background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.25)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#C0392B' },
  successMsg: { background: 'rgba(0,96,57,0.08)', border: '1px solid rgba(0,96,57,0.2)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#00563F' },
  btn: { background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  btnGolf: { background: 'linear-gradient(135deg,#9B7D52,#C19A6B)', color: 'white', border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  gameProfileHeader: { display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' },
  gameProfileIcon: { fontSize: 13, fontWeight: 700, color: '#00563F', background: 'rgba(0,96,57,0.08)', border: '1px solid rgba(0,96,57,0.2)', borderRadius: 8, padding: '3px 8px' },
  gameProfileName: { fontSize: 16, fontWeight: 700, color: '#1A1814', flex: 1 },
  verifiedBadge: { fontSize: 11, fontWeight: 700, color: '#00563F', background: 'rgba(0,96,57,0.08)', border: '1px solid rgba(0,96,57,0.2)', borderRadius: 8, padding: '3px 10px' },
  divider: { height: 1, background: '#F5F1E4' },
  accountLabel: { fontSize: 11, fontWeight: 700, color: '#7A7570', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 4 },
  accountValue: { fontSize: 15, color: '#1A1814', fontWeight: 500 },
  accountNote: { fontSize: 12, color: '#B0ACA4', marginTop: 2 },
  chevron: { fontSize: 12, color: '#7A7570', marginLeft: 4 },
  signOutBtn: { background: 'transparent', color: '#C0392B', border: '1.5px solid rgba(192,57,43,0.3)', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', width: '100%' },
}
